import { StackItem, Stack, Checkbox, Divider, Button, Container, Typography } from '@optimus-web/core';
import { LocalizedText, lt } from '@mews/framework';
import { LocalizedDateTimeFromUtc, ModalWrapper, NewModalBodyWrapper } from '@mews-commander/ui';
import { useResolveRoute } from '@mews/routing';
import { RouteName, TextKey } from '@mews-commander/enums';
import { ExportStateEnum, ExportTypeEnum } from '@mews-commander/data-access';
import { IconName } from '@optimus-web/icons';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { useExportDownload } from './useExportDownload';
import { useExportDetails } from './useExportDetails';
import { StatusTag } from './StatusTag';
import { ExportFilter, filtersSchema } from './types';

const parseFilters = (filters: string): ExportFilter[] => {
    try {
        const unsafeValue = JSON.parse(filters);
        const parsedResult = filtersSchema.parse(unsafeValue);
        return parsedResult[0];
    } catch {
        return [];
    }
};
export const ExportDetailsPage = ({ id }: { id: string }) => {
    const resolveRoute = useResolveRoute();
    const exportDownload = useExportDownload();
    const exportItem = useExportDetails(id, ExportTypeEnum.ReportExporter);
    const data = exportItem.data?.exports?.[0] ?? {};
    const exportFilters = parseFilters(data.filters ?? '');
    const breadcrumbs: Breadcrumbs = [
        {
            text: lt(TextKey.Exports),
            url: resolveRoute(RouteName.ExportIndex),
        },
        {
            text: lt(exportItem.data?.exports?.[0]?.reportTypeName as Mews.TextKey),
            active: true,
        },
    ];
    return (
        <ModalWrapper>
            <ModalHeader breadcrumbs={breadcrumbs} />
            <NewModalBodyWrapper padded>
                <Container width="50%" maxWidth="538px">
                    <Stack vertical spacing="300">
                        <Stack vertical spacing="200">
                            <StackItem>
                                <Typography textStyle="titleSmallStrong" as="div">
                                    <LocalizedText textKey={TextKey.ExportDetails} />
                                </Typography>
                            </StackItem>
                            <StackItem>
                                <Stack distribution="stretch">
                                    <StackItem>
                                        <LocalizedText textKey={TextKey.Status} />
                                    </StackItem>
                                    <StackItem>
                                        <StatusTag status={data.exportState || ExportStateEnum.Pending} note="" />
                                    </StackItem>
                                </Stack>
                            </StackItem>
                            <StackItem>
                                <Divider />
                            </StackItem>
                            <StackItem>
                                <Stack distribution="stretch">
                                    <StackItem>
                                        <LocalizedText textKey={TextKey.Created} />
                                    </StackItem>
                                    <StackItem>
                                        <LocalizedDateTimeFromUtc utcString={data.createdUtc || ''} />
                                    </StackItem>
                                </Stack>
                            </StackItem>
                            <StackItem>
                                <Divider />
                            </StackItem>
                            <StackItem>
                                <Stack distribution="stretch">
                                    <StackItem>
                                        <LocalizedText textKey={TextKey.Start} />
                                    </StackItem>
                                    <StackItem>
                                        <LocalizedDateTimeFromUtc utcString={data.startUtc || ''} />
                                    </StackItem>
                                </Stack>
                            </StackItem>
                            <StackItem>
                                <Divider />
                            </StackItem>
                            <Stack alignment="end">
                                <Button
                                    variant="primary"
                                    size="medium"
                                    icon={IconName.Download}
                                    iconPosition="right"
                                    disabled={data.fileSize === 0}
                                    onClick={() =>
                                        exportDownload.mutate({
                                            exportId: data.id || '',
                                            exportType: data.exportType || ExportTypeEnum.Manual,
                                        })
                                    }
                                    type="submit"
                                >
                                    <LocalizedText textKey={TextKey.DownloadReport} />
                                </Button>
                            </Stack>
                        </Stack>
                        <Stack vertical spacing="200">
                            <StackItem>
                                <Typography textStyle="titleSmallStrong" as="div">
                                    <LocalizedText textKey={TextKey.ReportFilters} />
                                </Typography>
                            </StackItem>

                            {exportFilters.map((item: ExportFilter) => {
                                let displayValue;
                                if (item.Type === 'boolean') {
                                    displayValue = <Checkbox checked={item.Value === 'true'} readOnly />;
                                } else if (Array.isArray(item.Value)) {
                                    displayValue = item.Value.join(', ');
                                } else {
                                    displayValue = item.Value;
                                }
                                return (
                                    <Stack vertical spacing="200" key={item.Name}>
                                        <StackItem>
                                            <Stack distribution="stretch">
                                                <StackItem>{item.Name}</StackItem>
                                                <StackItem>{displayValue}</StackItem>
                                            </Stack>
                                        </StackItem>
                                        <StackItem>
                                            <Divider />
                                        </StackItem>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Stack>
                </Container>
            </NewModalBodyWrapper>
        </ModalWrapper>
    );
};
